import Head from "expo-router/head";
import { useContext } from "react";

import {
  H1,
  H2,
  H3,
  LayoutContext,
  Link,
  List,
  Paragraph,
  ScrollView,
  Section,
  Separator,
  Text,
  Time,
  XStack,
  YStack,
  getTokenValue,
} from "@/components";
import { brandName } from "@/config/brand";

const summaryOfTermsListItems = {
  "What Does Curai Health Do?": [
    "Curai Health is an always available, purpose-built health agent that takes you from question to answer, action to comprehensive care. However, it is not a replacement for medical care.",
    "There is no cost to use our AI",
    "Curai Health can connect you to a primary care physician in the US via Curai Health Medical Group.",
  ],
  "What Curai Health Does NOT Do": [
    "NOT for emergencies or any sort: call 911 for any medical emergency",
    "NOT for mental health crises, thoughts of self-harm, or violence",
    "NOT for pediatric emergencies, pregnancy complications, or complex specialist conditions",
  ],
  "Your Obligations": [
    "By using Curai Health, you agree to arbitration instead of court for disputes",
    "We're a US-based service, international users assume responsibility for local law compliance",
  ],
  "Privacy & Data": [
    "We're HIPAA compliant and take your privacy seriously",
    "We don't share your data with third party AI models. Your data may be used for training our AI on how to serve you better and is anonymized for your protection",
    "Unless you create a user account, we don't save your data",
  ],
};

export default function Terms() {
  const { headerHeight } = useContext(LayoutContext);
  return (
    <>
      <Head>
        <title>{`Terms of Service — ${brandName}`}</title>
      </Head>
      <ScrollView
        padding="$200"
        width="100%"
        contentContainerStyle={{
          gap: getTokenValue("$300"),
          maxWidth: getTokenValue("$constraint"),
          marginHorizontal: "auto",
          paddingTop: headerHeight.current,
          paddingBottom: getTokenValue("$300"),
        }}
      >
        <YStack gap="$300" alignItems="center">
          <H1>Terms of Use</H1>
          <XStack>
            <Paragraph display="inline">Last updated:&nbsp;</Paragraph>
            <Time display="inline" dateTime="2026-02-27">
              February 27, 2026
            </Time>
          </XStack>
        </YStack>
        <Section>
          <H2 size="$lg" marginBottom="$50">
            Keeping it Simple - Summary of Terms
          </H2>
          {Object.entries(summaryOfTermsListItems).map(([label, items]) => (
            <YStack gap="$200" key={label}>
              <Text size="$md" bold>
                {label}
              </Text>
              <List listItems={items} />
            </YStack>
          ))}
        </Section>
        <Separator />
        <Section>
          <Paragraph>
            Curai Health is owned by Curai Inc. Curai, Inc. and the professional
            entities to which Curai, Inc. provides management services are
            pleased to offer you a platform for communicating electronically
            about your health. These Terms of Use govern your access to, and
            use, of the services available through the platform, including our
            website, our mobile application(s) and all content, information
            and/or materials provided in connection with the service.
          </Paragraph>
          <Paragraph>
            As explained above, these Terms contain a mandatory arbitration
            provision that requires the use of arbitration on an individual
            basis and limits the remedies available to you in the event of
            certain disputes.
          </Paragraph>
          <Paragraph>
            By clicking "I Accept" or by accessing or using our Services or
            creating an Account, you agree to these Terms. If you do not agree
            to these Terms, including the mandatory arbitration provision and
            class action waiver set forth herein, you may not access or use the
            Services.
          </Paragraph>
          <List
            listItems={[
              "The Services are not intended to provide instructions in the event of an emergency. If you believe your symptom or situation is urgent or life-threatening, call 911 or your local emergency medical system immediately.",
              "Do not delay or forgo seeking medical care from a health care provider if you have questions, concerns, or symptoms related to health topics or information that may be presented or referenced on the Services.",
              "Application of or reliance on any of the content, techniques, ideas, or suggestions accessed through the Services is at your sole discretion and risk.",
              "We do not recommend or endorse any specific drugs, tests, Providers, products, procedures, opinions, “off-label” drug uses, or any other information that may be provided through Services.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Nature of the Services
          </H2>
          <Paragraph>
            Some of our services have extra rules, called "Additional Terms." We
            will show you these rules when you try to use such a feature. By
            using that feature, you agree to its Additional Terms. If you don't
            agree, you can't use the feature. These extra rules are part of our
            main terms, but if there's a conflict between them, the Additional
            Terms for that specific feature will apply.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Privacy
          </H2>
          <Paragraph>
            Please refer to our <Link href="/privacy">Privacy Policy</Link> for
            information about how we collect, use and disclose information about
            you.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Eligibility to Use the Services
          </H2>
          <Paragraph>
            To use the Services, you must be at least 18 years old.
          </Paragraph>
          <Paragraph>
            Specifically, to be eligible to use the Services you must meet the
            following criteria and, by agreeing to these Terms, represent and
            warrant that you: (a) are 18 years of age or older; (b) have not
            been previously suspended or removed from the Services, or engaged
            in any activity that could result in suspension or removal from the
            Services; (c) will only maintain one Account at any given time; (d)
            have full power and legal authority to enter into these Terms and in
            doing so will not violate any other agreement to which you are a
            party, and will comply with these Terms at all times; and (e) agree
            to use your legal first name and your email address when registering
            (although you may use a preferred name or initials after
            registration when using the Services).
          </Paragraph>
          <Paragraph>
            If you are accessing or using our Services on behalf of another
            person or entity, you represent that you are authorized to accept
            these Terms on that person or entity's behalf and that the person or
            entity agrees to be responsible to us if you or the other person or
            entity violates these Terms
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Accounts, Account Security and Communication Preferences
          </H2>
          <Paragraph>
            You may need to register for a user account ("Account") to access
            some or all of our Services. It is your responsibility to prevent
            disclosure of your username and password, and to change your
            password if you feel that its security has been compromised. You may
            change your password at any time. We shall not be liable or
            responsible for any damages that result from your failure to keep
            your username and/or password secure. You are responsible for all
            activity in your Account. If you permit others to use your Account
            credentials, you are responsible for the activities of such users
            that occur in connection with your Account.
          </Paragraph>
          <Paragraph>
            As part of activating your Account, you are required to provide us
            with a valid email address and to update this address if you change
            your email account. By creating an Account, you also consent to
            receive electronic communications from Curai Health (e.g., via
            email, text message, or by posting notices to the Services). These
            communications may include operational notices about your Account
            (e.g., password changes and other transactional information) and are
            part of your relationship with us. You agree that any notices,
            agreements, disclosures or other communications that we send to you
            electronically will satisfy any legal communication requirements,
            including, but not limited to, that such communications be in
            writing. You should maintain copies of electronic communications
            from us by printing a paper copy or saving an electronic copy. We
            may also send you promotional communications via email, including,
            but not limited to, newsletters, special offers, surveys and other
            news and information we think will be of interest to you. You may
            opt out of receiving these promotional emails at any time by
            following the unsubscribe instructions.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Care Services
          </H2>
          <Paragraph>
            Dial 911 in the event of a medical emergency. The Services are not
            intended to provide instructions or help in the event of an
            emergency. If you believe your condition or situation is
            life-threatening, please call 911 or your emergency medical system
            immediately. Do not rely on or attempt to use the Services for any
            urgent medical needs.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Reservation of Rights
          </H2>
          <Paragraph>
            We can change, pause, or completely stop any part of our service at
            any time without giving you a heads-up. You agree that we aren't
            responsible if these changes cause problems for you or anyone else.
          </Paragraph>
          <Paragraph>Additionally, we have the right to:</Paragraph>
          <List
            listItems={[
              "End or suspend your access for any reason, without notice.",
              "Watch how you use the service to make sure you're following the rules.",
              "Investigate if we think you're misusing the service and work with the police.",
              "Share your information with law enforcement if they're investigating a crime or if we receive a court order.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Proprietary Rights
          </H2>
          <Paragraph>
            This section explains who owns the content on the service and what
            you are allowed to do with it.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            Our Content and Your Permission
          </H3>
          <List
            listItems={[
              "We Own It: Everything you see and interact with on our service (like text, logos, photos, videos, and software) belongs to us or our partners. It is protected by copyright and trademark laws.",
              "Your License: We give you a limited permission to use our service and its content for your own personal, non-commercial use.",
            ]}
          />
          <H3 size="$md" marginVertical="$50">
            What You CANNOT Do
          </H3>
          <Paragraph>
            Your permission to use the service comes with strict rules. You are
            not allowed to:
          </Paragraph>
          <List
            listItems={[
              "Sell or resell our service or content.",
              "Copy, share, or publicly display our content without our explicit permission.",
              "Change our content in any way, or remove our logos or copyright notices.",
              "Use data mining, bots, or any other automated tools to scrape information from our service.",
              "Use the service for anything other than its intended purpose.",
            ]}
          />
          <Paragraph>
            Breaking these rules may result in losing your access and could lead
            to legal action.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            Logos and Trademarks
          </H3>
          <List
            listItems={[
              'Our names (like "Curai Health Medical Group" and "Curai Health"), logos, and other trademarks belong to us. Other logos belong to their respective owners.',
              "You cannot use any of these names or logos without getting written permission first.",
            ]}
          />
          <H3 size="$md" marginVertical="$50">
            Your Feedback
          </H3>
          <Paragraph>
            If you send us any suggestions, ideas, or feedback, we are free to
            use them however we want, and we do not have to pay you or give you
            credit.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            User Supplied Material
          </H2>
          <Paragraph>
            When you share content like messages or photos ("User Content") on
            our service, you still own it.
          </Paragraph>
          <Paragraph>
            However, you give us a free, worldwide license to use, copy, modify,
            and share your content on any platform, now or in the future,
            without paying you.
          </Paragraph>
          <Paragraph>
            You promise that you have the legal right to share this content and
            that it doesn't violate any laws or anyone else's rights (like
            copyright). You are solely responsible for anything you post. While
            we can review or remove content, we don't check everything and are
            not responsible for what users post.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Appropriate Use of the Services
          </H2>
          <Paragraph>
            You agree to use the Services in accordance with all applicable
            local, state, national and foreign laws, treaties and regulations.
            You will not violate any contract, intellectual property or other
            third-party right or commit a tort, and you are solely responsible
            for your conduct while accessing or using our Services. In addition,
            without limitation, you will not, directly or indirectly, do any of
            the following while using or accessing the Services:
          </Paragraph>
          <List
            listItems={[
              "Engage in any harassing, threatening, intimidating, predatory or stalking conduct;",
              "Use or attempt to use another user's Account without authorization from that user and Curai Health Medical Group;",
              "Copy, reproduce, sell, rent, lease, distribute, transfer, modify or make derivative works from the Services or any Content;",
              "Remove, alter or tamper with any copyright, trademark or other proprietary rights or legal notices contained in any Content obtained from the Services;",
              "Decompile, disassemble, reverse engineer, or otherwise attempt to discover or derive any proprietary software code or information associated with the Services;",
              'Use the Services in any manner that could interfere in any way with the operation of the Services or any server, network or system associated with the Services, including, without limitation, by: hacking, mail-bombing, flooding, overloading, or making "denial of service" attacks; probing, scanning or testing the vulnerability of the Services or any server, network or system associated with the Services; breaching or circumventing firewall, encryption, security or authentication routines; accessing data not intended for you;',
              "Use any automated program, tool or process (including, without limitation, web crawlers, robots, bots, spiders, and automated scripts) to access the Services or any server, network or system associated with the Services, or to extract, collect, harvest or gather content or information from the Services;",
              "Frame or otherwise create a browser or border environment around any page or content of the Services, or deep-link to any internal page or area of the Services; or",
              "Make any other use of the Services that violates these Terms or any applicable law.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Links to Third Party Sites
          </H2>
          <Paragraph>
            We may link to other companies' websites or services, but we don't
            control them.
          </Paragraph>
          <List
            listItems={[
              "We don't endorse them: Just because we link to a service doesn't mean we approve of it or recommend it.",
              "We're not responsible: We make no guarantees about these third-party services and are not liable for anything that happens on them.",
              "Use them at your own risk: Your decision to use a linked service is entirely up to you.",
              "Their rules apply: When you use their service, you are subject to their terms and privacy policy, not ours.",
              "Contact them with problems: If you have any complaints or issues with a third-party service, you must contact them directly, not us.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Release
          </H2>
          <Paragraph>
            To the fullest extent permitted by applicable law, you release Curai
            Health and our owners, subsidiaries, affiliated companies,
            employees, shareholders, or directors (collectively, the "Curai
            Health Medical Group Parties") from responsibility, liability,
            claims, demands, and/or damages (actual and consequential) of every
            kind and nature, known and unknown (including, but not limited to,
            claims of negligence), arising out of or related to disputes between
            users and the acts or omissions of third parties. IF YOU ARE A
            CALIFORNIA RESIDENT, YOU HEREBY WAIVE ANY RIGHTS YOU MAY HAVE UNDER
            CALIFORNIA CIVIL CODE SECTION 1542, WHICH SAYS "A GENERAL RELEASE
            DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR OR RELEASING PARTY DOES
            NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF
            EXECUTING THE RELEASE AND THAT, IF KNOWN BY HIM OR HER, WOULD HAVE
            MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH THE DEBTOR OR
            RELEASED PARTY." You also waive any rights you may have under any
            other statute or common law principles that would otherwise limit
            the coverage of this release to include only those claims which you
            may know or suspect to exist in your favor at the time of agreeing
            to this release.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Indemnification
          </H2>
          <Paragraph>
            You agree to indemnify, defend and hold harmless the Curai Health
            Medical Group Parties from and against any and all damages,
            liabilities, fines, penalties, losses, expenses, fees, and costs
            (including without limitation reasonable attorneys' fees and costs)
            related to all claims, causes of action, charges, and
            investigations, arising out of or relating to: (a) your access to,
            or use or misuse of, the Services or the Content; (b) any User
            Content and/or User Feedback you submit or transmit; (c) your
            violation of any of these Terms; (d) any activity related to your
            registration by you or any other person accessing the Services
            through your Account, including, without limitation, negligent or
            wrongful conduct; (e) your conduct in connection with our Services
            and/or (f) your violation of any third party right, including,
            without limitation, any intellectual property right, publicity,
            confidentiality, property or privacy right.
          </Paragraph>
          <Paragraph>
            Curai Health Medical Group reserves the right, at our own expense,
            to assume the exclusive defense and control of any matter otherwise
            subject to indemnification by you, in which event you will cooperate
            with us in asserting any available defenses.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Disclaimer of Warranties
          </H2>
          <Paragraph>
            YOU EXPRESSLY AGREE THAT USE OF THE SERVICES AND CONTENT IS AT YOUR
            SOLE RISK. THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS
            AVAILABLE" BASIS. CURAI HEALTH EXPRESSLY DISCLAIMS ALL WARRANTIES OF
            ANY KIND, WHETHER EXPRESS OR IMPLIED, ANY WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR USE OR PURPOSE,
            NON-INFRINGEMENT, TITLE, OPERABILITY, CONDITION, QUIET ENJOYMENT,
            VALUE, AND ACCURACY OF DATA. CURAI MAKES NO WARRANTY THAT THE
            SERVICES OR CONTENT WILL MEET YOUR REQUIREMENTS, OR THAT THE
            SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR FREE; NOR
            DOES CURAI HEALTH MAKE ANY WARRANTY AS TO THE RESULTS THAT MAY BE
            OBTAINED FROM THE USE OF THE SERVICES OR CONTENT, OR THAT DEFECTS IN
            THE SERVICES OR CONTENT WILL BE CORRECTED, OR THAT ANY INFORMATION
            OBTAINED THROUGH OR IN CONNECTION WITH THE SERVICES OR CONTENT WILL
            BE ACCURATE OR COMPLETE. YOU UNDERSTAND AND AGREE THAT ANY MATERIAL
            DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE SERVICES IS
            DONE AT YOUR OWN DISCRETION AND RISK AND THAT YOU WILL BE SOLELY
            RESPONSIBLE FOR ANY DAMAGE OR LOSS OF DATA THAT RESULTS FROM THE
            DOWNLOAD OF SUCH MATERIAL AND/OR INFORMATION. NO ADVICE OR
            INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM CURAI
            HEALTH OR THROUGH THE SERVICES OR CONTENT WILL CREATE ANY WARRANTY
            NOT EXPRESSLY MADE HEREIN.
          </Paragraph>
          <Paragraph>
            The Services and Content are not intended to provide a substitute
            for your own sound and reasonably prudent judgment, and are not
            intended to be for diagnosis or treatment. Persons using the
            Services and Content assume full responsibility for the use of the
            Services, Content, materials and other information provided, and
            agree that Curai Health Medical Group is not responsible or liable
            for any claim, loss, or damage arising from their use. Your reliance
            on the Services and Content obtained or used by you is solely at
            your own risk except as provided by law.
          </Paragraph>
          <Paragraph>
            Some states may not allow the exclusion of implied warranties, so
            the above exclusion may not apply to you. But, to the extent
            permitted by applicable law, we exclude all warranties.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Limitation of Liability
          </H2>
          <Paragraph>
            TO THE EXTENT PERMITTED UNDER APPLICABLE LAW, IN NO EVENT WILL THE
            CURAI HEALTH PARTIES BE LIABLE TO YOU FOR ANY DIRECT, INDIRECT,
            INCIDENTAL, SPECIAL, PUNITIVE, CONSEQUENTIAL OR EXEMPLARY DAMAGES,
            INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF REVENUES,
            PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES (EVEN IF
            SUCH PARTIES WERE ADVISED OF, KNEW OF OR SHOULD HAVE KNOWN OF THE
            POSSIBILITY OF SUCH DAMAGES, AND NOTWITHSTANDING THE FAILURE OF
            ESSENTIAL PURPOSE OF ANY LIMITED REMEDY), ARISING OUT OF OR RELATED
            TO YOUR USE OF THE SERVICES OR ANY CONTENT, REGARDLESS OF WHETHER
            SUCH DAMAGES ARE BASED ON CONTRACT, TORT (INCLUDING NEGLIGENCE AND
            STRICT LIABILITY), WARRANTY, STATUTE OR OTHERWISE. TO THE FULLEST
            EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE MAXIMUM
            AGGREGATE LIABILITY OF THE CURAI HEALTH PARTIES (JOINTLY) ARISING
            OUT OF OR IN ANY WAY RELATED TO ACCESS OR USE OF THE SERVICES OR
            CONTENT, EXCEED THE GREATER OF $100 OR THE AMOUNT YOU PAID TO US IN
            THE ONE (1) MONTH PERIOD IMMEDIATELY PRECEDING THE DATE ON WHICH
            YOUR CLAIM AROSE.
          </Paragraph>
          <Paragraph>
            You further agree that the limitations of liability set forth above
            will survive any termination or expiration of these Terms and will
            apply even if any limited remedy specified herein is found to have
            failed its essential purpose. Some jurisdictions do not allow the
            exclusion of certain warranties or the limitation or exclusion of
            liability for incidental or consequential damages, and as a result
            some of the above disclaimers may not apply to you. To the extent we
            may not, as a matter of applicable law, disclaim any implied
            warranty or limit its liabilities, the scope and duration of such
            warranty and the extent of our liability shall be the minimum
            permitted under applicable law.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Mutual Rights of Termination
          </H2>
          <Paragraph>
            In simple terms, this section is about closing your account:
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            Who can end the agreement?
          </H3>
          <List
            listItems={[
              "You: You can close your account at any time, for any reason, simply by telling us.",
              "Us: We can suspend or close your account at any time, for any reason, without giving you prior notice.",
            ]}
          />
          <H3 size="$md" marginVertical="$50">
            What happens when an account is closed?
          </H3>
          <List
            listItems={[
              "No Refunds: You will not get any money back.",
              "Data Deletion: Your information and any content you uploaded may be permanently deleted.",
              "Health Records Exception: We will still provide you with access to your health records, as we are legally and ethically required to do.",
              "No Liability: You agree not to hold us responsible for any issues that arise from your account being closed or your data being deleted.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Dispute Resolution; Arbitration
          </H2>
          <Paragraph>
            In simple terms, this section is about how you and Curai Health
            Medical Group will handle legal disagreements.
          </Paragraph>
          <Paragraph>
            This is a binding legal agreement. By using the service, you are
            giving up your right to sue in court and to participate in a
            class-action lawsuit.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            You Agree to Arbitration Instead of Court
          </H3>
          <Paragraph>
            For almost all disagreements, we both agree not to go to a regular
            court with a judge or jury. Instead, we will use a process called
            binding arbitration, where a neutral expert (an arbitrator) hears
            both sides and makes a final, legally-binding decision.
          </Paragraph>
          <Paragraph>
            Exceptions: You can still go to small claims court for minor issues.
            This arbitration rule also doesn't apply to disputes about
            intellectual property (like someone stealing a trademark or patent,
            including copyrights, trademarks, trade names, logos, trade secrets
            or patents.)
          </Paragraph>
          <Paragraph>
            You also agree that this agreement affects interstate commerce and
            that the enforceability of this section is governed and enforced in
            accordance with the Federal Arbitration Act, 9 U.S.C. § 1 et seq.
            (the "FAA"),
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            No Class-Action Lawsuits
          </H3>
          <Paragraph>
            All disagreements must be handled on an individual basis, only
            between you and us.
          </Paragraph>
          <Paragraph>
            You agree not to join or start any class-action lawsuit, group
            arbitration, or any other kind of case where you represent other
            people.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            You Must Try to Resolve Disputes Informally First
          </H3>
          <Paragraph>
            Before starting arbitration, you have to try to solve the problem
            with us directly.
          </Paragraph>
          <Paragraph>
            You must send us a formal written notice describing your complaint
            within 30 days of the issue happening.
          </Paragraph>
          <Paragraph>
            We then have 30 days to work together to find a solution.
          </Paragraph>
          <Paragraph>
            If we can't solve it in that time, then either of us can begin the
            arbitration process.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            How the Arbitration Process Works
          </H3>
          <Paragraph>
            This section explains the specific rules for how a disagreement
            would be handled in arbitration.
          </Paragraph>
          <List
            listItems={[
              "Location: The arbitration can happen in one of three places: over the phone, in the county where you live, or in Santa Clara County, California.",
              "Procedure: The process will be private and confidential. A single, neutral arbitrator will make the decision based on the rules of the American Arbitration Association (AAA).",
              "Choosing the Arbitrator: We will first try to agree on an arbitrator together. If we can't agree within 30 days, the AAA has a process for selecting one.",
              "Costs: You are responsible for your own legal costs, and we are responsible for ours. The arbitrator cannot force one side to pay the other's fees.",
              "If You Can't Afford It: If the arbitration fees are too expensive for you, you must give us the option to pay them for you before you try to file a lawsuit in court.",
              "Evidence: Both you and Curai Health Medical Group will have the chance to request and gather relevant information and documents from each other.",
            ]}
          />
          <H3 size="$md" marginVertical="$50">
            The Arbitrator's Power and Limits
          </H3>
          <List
            listItems={[
              "The Arbitrator is in Charge: The arbitrator has the final say on all decisions regarding the dispute, including whether the issue should be arbitrated in the first place. They can award the same damages or remedies that a judge in a court could.",
              "Strictly Individual Cases: The arbitrator is not allowed to handle any class-action or group claims. They can only resolve your individual dispute and cannot combine your case with anyone else's.",
            ]}
          />
          <H3 size="$md" marginVertical="$50">
            Understanding the Rules
          </H3>
          <Paragraph>
            You are responsible for knowing the AAA's rules for arbitration. By
            agreeing to these terms, you are confirming that you have had the
            chance to read and understand them.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            If a Part of this Agreement is Invalid
          </H3>
          <Paragraph>
            If a court decides that any single part of this arbitration section
            is illegal or unenforceable, the rest of the section will still
            apply and remain in effect.
          </Paragraph>
          <H3 size="$md" marginVertical="$50">
            Most Importantly: You Can Opt Out
          </H3>
          <Paragraph>
            You have the right to reject this entire arbitration agreement.
          </Paragraph>
          <List
            listItems={[
              "To opt out, you must send a physical letter to the Legal Dept, Curai, Inc., 2443 Fillmore St. #380-15799, San Francisco, CA 94114. within 30 days of first accepting these terms.",
              "Your letter must include your full name and clearly state that you want to opt out of binding arbitration.",
              "If you opt out, disagreements will be handled in court as they normally would.",
            ]}
          />
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Limitation of Time to File Claims
          </H2>
          <Paragraph>
            You agree that any Dispute must be commenced or filed by you within
            one (1) year of the date the Dispute arose, otherwise the underlying
            claim is permanently barred (which means that you will no longer
            have the right to assert such claim regarding the Dispute).
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Applicable Law; Venue
          </H2>
          <Paragraph>
            These Terms, and your access to and use of the Services shall be
            governed by and construed and enforced in accordance with the laws
            of the State of California, without regard to conflict of law rules
            or principles (whether of the State of California or any other
            jurisdiction) that would cause the application of the laws of any
            other jurisdiction. Any Dispute between the parties that is not
            subject to arbitration or cannot be heard in small claims court,
            shall be resolved on an individual basis in the state or federal
            courts of the State of California and the United States,
            respectively, sitting in the State of California, County of Santa
            Clara. You may litigate a Dispute in the small claims court located
            in the county of your billing address if the Dispute meets the
            requirements to be heard in small claims court.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            International Users
          </H2>
          <Paragraph>
            If you are located outside of the United States and access the
            Services or submit your information to us, United States law may not
            offer the same privacy protections as the law of your jurisdiction.
            If you visit our Services or contact us from outside of the United
            States, please be advised that (a) any information you provide to us
            or that we automatically collect will be transferred to the United
            States; and (b) that by using our Services or submitting
            information, you explicitly authorize its transfer to and subsequent
            processing in the United States in accordance with our Privacy
            Policy.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Changes to these Terms
          </H2>
          <Paragraph>
            We reserve the right to change our Terms at any time. Any changes
            that we make will become a part of our agreement with you when they
            are posted to our Terms of Service. Your continued use of our
            services will constitute your agreement to the changes we have made.
            If we make material changes, we will post the amended Terms to our
            Terms of Service. We may also notify you by sending an email
            notification to the address associated with your Account or
            providing notice through our Digital Services. Revisions are
            effective and binding when posted on the Terms of Service. The "Last
            Updated" date at the top of these Terms indicates the last date
            these Terms were revised.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Apple Device Additional Terms
          </H2>
          <Paragraph>
            If you access an App via a mobile device or tablet branded by Apple,
            Inc. ("Apple") and running Apple's iOS (an "Apple Device"), the
            following terms ("Apple Terms") are hereby made part of these Terms:
          </Paragraph>
          <Paragraph>
            If these Apple Terms conflict with any other provision of these
            Terms, then the Apple Terms control with respect to access and use
            of the Digital Services via an Apple Device. Apple is not a party to
            these Terms and does not own and is not responsible for any App you
            access via your Apple Device ("iOS App") and the content thereof.
            The Apple App Store Terms of Service (the "App Store Terms of
            Service") control to the extent of any conflict with these Terms
            with respect to an iOS App. The license granted to use an iOS App is
            limited to a non-transferable license to use the App on an Apple
            Device that you own or control and as permitted by the usage rules
            set forth in the App Store Terms of Service. Apple has no obligation
            whatsoever to furnish any maintenance and support services with
            respect to any iOS App. In the event of any failure of the iOS App
            to conform to any applicable warranty provided by Curai Health
            Medical Group in these Terms, you may notify Apple and Apple will
            refund the purchase price for the iOS App (if any). To the maximum
            extent permitted by applicable law, Apple will have no other
            warranty obligation whatsoever with respect to the iOS App and will
            not be responsible for any other claims, losses, liabilities,
            damages, costs or expenses attributable to any failure to conform to
            any warranty. Apple is not responsible for addressing your or any
            third-party claims relating to an iOS App and/or use of an iOS App,
            including, but not limited to: (i) product liability claims; (ii)
            any claim that an iOS App fails to conform to any applicable legal
            or regulatory requirement; and (iii) claims arising under consumer
            protection or similar legislation. It is not the intent of these
            Terms to limit Curai Health Medical Group's liability beyond what is
            permitted by applicable law. In the event of any third party claim
            that an iOS App or your possession and use (in accordance with these
            Terms) of an iOS App infringes that third party's intellectual
            property rights, Apple will not be responsible for the
            investigation, defense, settlement and discharge of any such
            intellectual property infringement claim. In addition to the
            requirements of Section 3, you represent and warrant that (i) you
            are not located in a country that is subject to a U.S. Government
            embargo, or that has been designated by the U.S. Government as a
            "terrorist supporting" country; and (ii) you are not listed on any
            U.S. Government list of prohibited or restricted parties. Apple, and
            Apple's subsidiaries, are third-party beneficiaries of these Terms
            with respect to any iOS App, and that, upon your acceptance of these
            Terms, Apple has the right (and deemed to have accepted the right)
            to enforce the Terms against you with respect to the iOS App as a
            third-party beneficiary thereof. Please direct any questions,
            complaints or claims with respect to an iOS App to Curai Health
            Medical Group at the contact information provided below.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Severability
          </H2>
          <Paragraph>
            If any provision of these Terms is found by a court of competent
            jurisdiction or arbitrator to be invalid, the parties nevertheless
            agree that the court should endeavor to give the effect to the
            parties' intentions as reflected in the provision, and that the
            other provisions of these Terms remain in full force and effect.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Miscellaneous
          </H2>
          <Paragraph>
            These Terms, and any other terms and policies incorporated herein,
            constitute the entire agreement between you and Curai Health Medical
            Group relating to your access to and use of the Services. These
            Terms, and any rights and licenses granted hereunder, may not be
            transferred or assigned by you without the prior written consent of
            Curai Health Medical Group. No waiver of any provision of these
            Terms will constitute a waiver of such provision in any prior,
            concurrent or subsequent circumstance, and Curai Health Medical
            Group's failure to assert any right or provision under these Terms
            shall not constitute a waiver of such right or provision. Except as
            otherwise provided herein, these Terms are intended solely for the
            benefit of the parties and are not intended to confer third party
            beneficiary rights upon any other person or entity.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Use of AI Translation
          </H2>
          <Paragraph>
            Curai Health uses software-based translation to deliver content /
            its service in other languages besides English. By opting in to use
            Curai Health in a non-English language, you understand and
            acknowledge that no automated translation is perfect, nor is it
            intended to replace human translation efforts. While Curai Health
            makes its best efforts to ensure that content is accessible in other
            languages, Curai Health does not guarantee the accuracy of the
            translated text and some text may not be accurately translated due
            to the limitations of the translation tool. Some content such as
            text in images, PDF files, or other document types cannot be
            translated. Curai Health shall not be held liable for any errors,
            omissions, inaccuracies or harm that may occur due to dated or
            incorrect translations provided by the translation tool. The
            original source language is English for all content. Any
            discrepancies or differences created in the translation are not
            binding and have no legal effect for compliance or enforcement
            purposes.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Contact Us
          </H2>
          <Paragraph>
            Please contact us at{" "}
            <Link href="mailto:legal@curai.com">legal@curai.com</Link> with any
            questions regarding these Terms.
          </Paragraph>
          <Text size="$sm" color="$textSecondary">
            V. 1.0 2.27.26
          </Text>
        </Section>
      </ScrollView>
    </>
  );
}
